import React from 'react'
import Head from 'next/head'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { wrapper } from '../../store/configStore'
import { SERVER_IP } from '../../store/middleware/api'
import { productsReceived, productsSetFilters } from '../../store/slices/entities/products'
import LiveStreamingProductionPage from '../../components/design-system/marketing/LiveStreamingProductionPage'
import { liveStreamingProductionFallback } from '../../content/seo/liveStreamingProduction'
import { requestPageData } from '../../utilities/boundedPageData'

const getLiveStreamingSeoData = async ({ preview, previewData }) => {
  const response = await requestPageData({
    url: `${SERVER_IP}/seo/pages/${preview ? 'preview' : 'published'}`,
    params: preview ? {
      path: liveStreamingProductionFallback.page.path,
      token: previewData?.seoPreviewToken
    } : {
      path: liveStreamingProductionFallback.page.path
    },
    fallback: null,
    label: `SEO data for ${liveStreamingProductionFallback.page.path}`
  })

  if (!response.data) {
    return { seoData: liveStreamingProductionFallback, usedFallback: true }
  }

  const data = response.data.data || {}
  const componentContent = data.revision?.content?.custom?.liveStreamingProduction
  return {
    seoData: {
      page: {
        path: data.page?.path || liveStreamingProductionFallback.page.path,
        canonicalUrl: data.page?.canonicalUrl || liveStreamingProductionFallback.page.canonicalUrl
      },
      revision: componentContent
        ? {
            seo: data.revision?.seo,
            content: { custom: { liveStreamingProduction: componentContent } }
          }
        : {},
      source: preview ? 'draft-preview' : 'published'
    },
    usedFallback: false
  }
}

export const getStaticProps = wrapper.getStaticProps(
  async ({ store, preview, previewData }) => {
    const [productResponse, reviewResponse, seoResponse] = await Promise.all([
      requestPageData({
        required: true,
        validateData: Array.isArray,
        url: `${SERVER_IP}/inventory/product`,
        params: {
          msr: true,
          cat: 'Cameras'
        },
        label: `camera products for ${liveStreamingProductionFallback.page.path}`
      }),
      requestPageData({
        url: `${SERVER_IP}/google-review/public`,
        params: {
          page: liveStreamingProductionFallback.page.path,
          limit: 3
        },
        fallback: null,
        label: `reviews for ${liveStreamingProductionFallback.page.path}`
      }),
      getLiveStreamingSeoData({ preview, previewData })
    ])

    const bundles = JSON.parse(JSON.stringify((Array.isArray(productResponse.data) ? productResponse.data : [])
      .filter(product => product.categories?.includes('Visuals/Cameras') && product.name?.includes('Bundle'))
      .map(product => ({
        _id: product._id,
        allow_pickup: product.allow_pickup,
        categories: product.categories,
        description: product.description,
        difficulty: product.difficulty,
        marketing_priority: product.marketing_priority,
        name: product.name,
        only_orlando: product.only_orlando,
        only_purchase: product.only_purchase,
        orlando: product.orlando,
        picture: product.picture,
        price: product.price,
        requires_tech: product.requires_tech,
        requires_two: product.requires_two,
        snippets: product.snippets
      }))))
    store.dispatch({ type: productsReceived.type, payload: bundles })
    store.dispatch({ type: productsSetFilters.type, payload: { static: true } })

    return {
      props: {
        bundles,
        reviewData: reviewResponse.data,
        seoData: seoResponse.seoData,
        isPreview: Boolean(preview)
      },
      revalidate: productResponse.usedFallback || reviewResponse.usedFallback || seoResponse.usedFallback
        ? 60
        : 3600
    }
  }
)

const mergeLiveStreamingContent = stored => {
  const fallback = liveStreamingProductionFallback.revision.content
  const next = stored?.custom?.liveStreamingProduction || {}
  const mergeObject = key => ({ ...fallback[key], ...(next[key] || {}) })
  const mergeItems = key => ({
    ...mergeObject(key),
    items: next[key]?.items?.length ? next[key].items : fallback[key].items
  })
  const hero = mergeObject('hero')
  const process = mergeObject('process')
  const risk = mergeItems('risk')

  // Preserve future CMS image changes while upgrading the legacy hero asset.
  if (hero.image?.src === 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1713330604013.jpg') {
    hero.image = fallback.hero.image
    hero.imageAlt = fallback.hero.imageAlt
  }

  // Preserve future CMS image changes while upgrading the legacy process asset.
  if (process.image === 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1713369856736.jpg') {
    process.image = fallback.process.image
    process.imageAlt = fallback.process.imageAlt
  }

  // Preserve future CMS image changes while upgrading the legacy readiness asset.
  if (risk.image === 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1713330500129.jpg') {
    risk.image = fallback.risk.image
    risk.imageAlt = fallback.risk.imageAlt
  }

  return {
    ...fallback,
    ...next,
    hero,
    proof: next.proof?.length ? next.proof : fallback.proof,
    paths: {
      ...mergeObject('paths'),
      managed: { ...fallback.paths.managed, ...(next.paths?.managed || {}) },
      rental: { ...fallback.paths.rental, ...(next.paths?.rental || {}) }
    },
    eventTypes: mergeItems('eventTypes'),
    capabilities: mergeItems('capabilities'),
    process: {
      ...process,
      steps: next.process?.steps?.length ? next.process.steps : fallback.process.steps
    },
    risk,
    caseStudies: next.caseStudies?.length ? next.caseStudies : fallback.caseStudies,
    faqs: next.faqs?.length ? next.faqs : fallback.faqs,
    proposal: mergeObject('proposal'),
    builder: mergeObject('builder'),
    bundles: mergeObject('bundles'),
    related: mergeItems('related')
  }
}

const LiveStreaming = ({ bundles, reviewData, seoData }) => {
  const page = seoData?.page || liveStreamingProductionFallback.page
  const revision = seoData?.revision || liveStreamingProductionFallback.revision
  const hasComponentContent = Boolean(revision.content?.custom?.liveStreamingProduction)
  const seo = {
    ...liveStreamingProductionFallback.revision.seo,
    ...(hasComponentContent ? (revision.seo || {}) : {})
  }
  const content = mergeLiveStreamingContent(revision.content)
  const canonical = seo.canonicalUrl || page.canonicalUrl
  const openGraph = {
    type: 'website',
    url: seo.openGraph?.url || canonical,
    title: seo.openGraph?.title || seo.title,
    description: seo.openGraph?.description || seo.description,
    images: seo.openGraph?.images || liveStreamingProductionFallback.revision.seo.openGraph.images,
    site_name: 'Miami Sound Rental'
  }
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${canonical}#service`,
    name: 'Live Streaming and Hybrid Event Production',
    serviceType: 'Managed live streaming, webcast, and hybrid event production',
    url: canonical,
    description: seo.description,
    provider: { '@id': 'https://miamisoundrental.com/' },
    areaServed: [
      { '@type': 'City', name: 'Miami' },
      { '@type': 'City', name: 'Fort Lauderdale' },
      { '@type': 'AdministrativeArea', name: 'South Florida' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Live streaming production capabilities',
      itemListElement: content.capabilities.items.map(item => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: item.title, description: item.description }
      }))
    }
  }

  return (
    <>
      <NextSeo
        title={seo.title}
        description={seo.description}
        canonical={canonical}
        noindex={seo.robots?.index === false}
        nofollow={seo.robots?.follow === false}
        additionalMetaTags={[{
          name: 'keywords',
          content: [seo.primaryKeyword, ...(seo.secondaryKeywords || [])].filter(Boolean).join(', ')
        }]}
        twitter={{ handle: '@miamisoundrent1', site: '@miamisoundrent1', cardType: seo.twitter?.cardType || 'summary_large_image' }}
        openGraph={openGraph}
      />
      <BreadcrumbJsonLd itemListElements={[
        { position: 1, name: 'Home', item: 'https://miamisoundrental.com/' },
        { position: 2, name: 'Live Streaming Production', item: canonical }
      ]} />
      <Head>
        <script key='live-streaming-service-schema' type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      </Head>
      <LiveStreamingProductionPage bundles={bundles} content={content} reviewData={reviewData} />
    </>
  )
}

export default LiveStreaming
